import React, { useContext, useState, useEffect } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
  makeStyles,
  Theme,
} from '@material-ui/core';
import { FormProvider, useForm } from 'react-hook-form';
import { TbPencil } from 'react-icons/tb';

import { DeviceContext } from '@vyce/core/src/contexts/deviceContext';
import { sendVerificationSMSRequest } from '@vyce/core/src/api/auth';
import { changePhoneRequest } from '@vyce/core/src/api/users';
import { VerifyPhoneData } from '@vyce/core/src/types';
import { VerifyPhone } from '../../auth/components/VerifyPhone';
import { MAIN_CONTAINER_ID } from '@vyce/core/src/constants';
import { changePhoneByAdminRequest } from '@vyce/core/src/api/legend/users';
import { NotificationContext } from '../../../contexts/notificationContext';
import { PhoneNumberFieldV2 } from '../../../components/controlled-inputs/PhoneNumberFieldV2';
import { AppIconButton } from '../../../components/AppIconButton';
import { ButtonTitleWithLoading } from '../../../components/ButtonTitleWithLoading';

interface Props {
  phone?: string;
  verified: boolean;
  isLegend?: boolean;
  userId: string;
  verifyPhone?: (data: VerifyPhoneData) => void;
  getReCaptchaToken: () => Promise<string>;
  /** Called after the phone is successfully changed, with the new value. */
  onSaved?: (phone: string) => void;
}

interface Form {
  phone: string;
}

export const useStyles = makeStyles((theme: Theme) => ({
  dialogContent: {
    overflowY: 'unset',
  },
}));

export const EditPhoneOrVerify: React.FC<Props> = ({
  verified: outerVerified,
  phone: outerPhone,
  isLegend,
  userId,
  verifyPhone,
  getReCaptchaToken,
  onSaved,
}) => {
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const { isMobile } = useContext(DeviceContext);
  const theme = useTheme();
  const classes = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const [phone, setPhone] = useState<string>(outerPhone ?? '');
  const [verified, setVerified] = useState<boolean>(outerVerified ?? false);
  const [openVerifyPhoneDialog, setOpenVerifyPhoneDialog] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const methods = useForm<Form>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      phone: phone,
    },
  });
  const handleOpen = () => {
    const nextPhone = outerPhone ?? phone ?? '';
    methods.reset({ phone: nextPhone });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const sendVerificationCode = async () => {
    try {
      const recaptcha_token = await getReCaptchaToken();
      await sendVerificationSMSRequest({ recaptcha_token });
      setOpen(false);
      setOpenVerifyPhoneDialog(true);
    } catch (e) {
      handleServerError(e);
    }
  };

  const onSubmit = async (data: Form, event?: React.BaseSyntheticEvent<object, any, any>) => {
    event?.preventDefault();
    try {
      setLoading(true);
      const recaptcha_token = await getReCaptchaToken();
      if (isLegend) {
        await changePhoneByAdminRequest({ phone: data.phone, user_id: userId, recaptcha_token });
      } else {
        await changePhoneRequest(data.phone);
        sendVerificationCode();
      }
      setPhone(data.phone);
      setVerified(false);
      setOpen(false);
      onSaved?.(data.phone);
      return false;
    } catch (e) {
      if ((e as any).status === 409) {
        return showNotification({
          message:
            'This mobile number is already registered to a Vyce account. Please use another number for this profile or login to your existing account. ',
          options: { variant: 'error' },
        });
      }
      setOpen(false);
      handleServerError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setVerified(outerVerified);
  }, [outerVerified]);

  useEffect(() => {
    setPhone(outerPhone ?? '');
  }, [outerPhone]);

  return (
    <>
      <Box display="flex" height="100%" alignItems="center" justifyContent="space-between">
        <Box>
          <Box display="flex">
            <Typography variant="subtitle2" color="textSecondary">
              Phone{' '}
            </Typography>
            <Typography
              style={{
                color: !verified ? theme.palette.error.main : theme.palette.text.secondary,
                marginLeft: 4,
              }}
              variant="subtitle2"
            >
              ({verified ? 'verified' : 'unverified'})
            </Typography>
            <Typography variant="subtitle2">:</Typography>
          </Box>
          <Typography variant="caption" style={{ fontWeight: 500 }}>
            {phone}
          </Typography>
        </Box>

        <Box display="flex" height="100%" gridGap={8}>
          {!verified && phone && !isLegend && (
            <Button variant="outlined" size="small" onClick={sendVerificationCode} color="primary">
              Verify
            </Button>
          )}

          <AppIconButton variant="primary" onClick={handleOpen}>
            <TbPencil color={theme.palette.primary.dark} size="18px" />
          </AppIconButton>
        </Box>
      </Box>

      {verifyPhone && (
        <Dialog
          container={document.getElementById(MAIN_CONTAINER_ID)}
          open={openVerifyPhoneDialog}
          onClose={() => setOpenVerifyPhoneDialog(false)}
        >
          <Box padding={4}>
            <VerifyPhone close={() => setOpenVerifyPhoneDialog(false)} verifyPhone={verifyPhone} />
          </Box>
        </Dialog>
      )}

      <Dialog fullScreen={isMobile} open={open} onClose={handleClose}>
        <FormProvider {...methods}>
          <form>
            <DialogTitle>Change your Mobile Number</DialogTitle>
            <DialogContent className={classes.dialogContent}>
              Please input your new Mobile Number and we’ll send you a verification code.
              <PhoneNumberFieldV2 name="phone" autoFocus margin="normal" label="Mobile Number" />
            </DialogContent>
            <DialogActions>
              <Button size="large" variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                size="large"
                type="button"
                style={{ width: 100 }}
                disabled={loading || !!Object.keys(methods.formState.errors || {}).length}
                color="primary"
                variant="contained"
                onClick={() => onSubmit(methods.getValues())}
              >
                <ButtonTitleWithLoading title="Save" loaderVariant="paper" loading={loading} />
              </Button>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
    </>
  );
};
